// src/pwa/InstallCard.tsx: the install sheet, the owner's pick (docs/DECISIONS.md, "Install prompt"; direction B, "a
// bottom sheet that shows the result"). It rises after the family's moment (a message sent, Start Your Build's Sent
// page, a second visit): their home screen with the builder's icon on it, one line, Not Now and one navy button.
//   Android (android-prompt)          the button opens Chrome's own install dialog, straight from the tap.
//   iPhone (ios-safari, ios-other-browser)  the same sheet then walks the three taps in place (Share, Add to Home
//                                     Screen, Add), worded for Safari 26, earlier Safari, or Chrome and Edge.
//   in-app browsers                   "Open this page in Safari first" with Copy Link (iOS), or Open in Chrome
//                                     (Android, an intent link).
// Not Now, the scrim, Esc and a drag down put it away: before the button that is a snooze (src/pwa/prompt.ts), after
// it they just close. A sheet shown again after a move (Sort hides it) resumes the iPhone's steps. No "Don't ask again": three cards is the most a device ever sees. The line promises a ping only once
// PWA_MODE allows push (InstallArt.tsx, pitchLine). A centred dialog at 1024px and up (an iPad held sideways).
import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { tenant } from "../state/session";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";
import { toast } from "../ui/Toasts";
import { HomeScreen, pitchLine, StepArt, StepBars, stepLine, type InstallStep } from "./InstallArt";
import { useInstallPrompt, type InstallCard as Card } from "./prompt";
import "../styles/install.css";

/** The sheet's slide away (direction.css, --t-med) before the card is answered, which unmounts it. */
const LEAVE_MS = 260;

export function InstallCard(props: { card: Card }) {
  const s = useInstallPrompt();
  const t = tenant.value;
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState<0 | InstallStep>(() => (props.card.accepted && props.card.platform.startsWith("ios-") ? 1 : 0));
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  // The card is a new object whenever the prompt's state changes; the latest one answers.
  const card = useRef(props.card);
  card.current = props.card;
  const leaving = useRef(false);
  /** How far the page was lifted for the message just sent (scrolled back as the sheet leaves). */
  const lifted = useRef(0);

  // On screen: from now it counts toward this device's limits (once, however often the sheet re-renders).
  useEffect(() => {
    card.current.shown();
  }, []);

  // After a message, the message stays in sight: once the sheet is up, the page scrolls it into the space above,
  // with room added at the page's end while the sheet is up (Messages marks it data-install-keep). Its top never goes
  // under the thread's sticky head (a short phone keeps the top of a tall message). Only for the bottom sheet: the
  // centred dialog at 1024px and up leaves the thread in view.
  useEffect(() => {
    if (props.card.trigger !== "message") return;
    const root = document.documentElement;
    const timer = window.setTimeout(() => {
      if (leaving.current || (typeof window.matchMedia === "function" && window.matchMedia("(min-width: 1024px)").matches)) return;
      const keep = document.querySelector<HTMLElement>("[data-install-keep]");
      const sheet = document.querySelector<HTMLElement>(".hb-install.sheet");
      if (!keep || !sheet) return;
      const k = keep.getBoundingClientRect();
      const head = document.querySelector<HTMLElement>(".thread-head")?.getBoundingClientRect().bottom ?? 0;
      const over = Math.ceil(Math.min(k.bottom + 16 - sheet.getBoundingClientRect().top, k.top - head - 12));
      if (over <= 0) return;
      lifted.current = over;
      root.style.setProperty("--ins-lift", `${over}px`);
      root.classList.add("hb-install-lift");
      window.scrollBy({ top: over, behavior: "smooth" });
    }, LEAVE_MS + 40);
    return () => {
      window.clearTimeout(timer);
      root.classList.remove("hb-install-lift");
      root.style.removeProperty("--ins-lift");
    };
  }, []);

  if (!t) return null;

  /** Slides away (and the page back down, if it was lifted), then answers. */
  const leave = (answer: (c: Card) => void) => {
    if (leaving.current) return;
    leaving.current = true;
    setOpen(false);
    if (lifted.current > 0) window.scrollBy({ top: -lifted.current, behavior: "smooth" });
    lifted.current = 0;
    window.setTimeout(() => answer(card.current), LEAVE_MS);
  };

  // Called straight from the tap: Chrome's dialog, and the clipboard, only open for a tap.
  const copyLink = () => {
    if (leaving.current) return;
    const href = s.pageHref;
    if (!href) return;
    try {
      void navigator.clipboard.writeText(href).then(
        () => {
          setCopied("yes");
          toast("Link copied. Paste it into Safari.");
        },
        () => setCopied("failed"),
      );
    } catch {
      setCopied("failed");
    }
  };
  const accept = () => {
    if (leaving.current) return;
    void card.current.accept().then((outcome) => {
      if (outcome === "guide") setStep(1);
      else if (outcome === "installed") toast("Added to your home screen.");
    });
  };

  const platform = props.card.platform;
  const inApp = platform === "in-app-browser";
  const guide = s.guide ?? "safari";
  const label = step === 0 ? "Add the App" : `Step ${step} of 3`;

  let main: JSX.Element;
  if (inApp && s.browserHref) {
    main = (
      <Button class="ins-main" href={s.browserHref} icon="arrow-ur" onClick={() => void (leaving.current || card.current.accept())}>
        Open in Chrome
      </Button>
    );
  } else if (inApp) {
    main = (
      <Button
        class="ins-main"
        icon={copied === "yes" ? "check" : "link"}
        onClick={() => {
          if (leaving.current) return;
          copyLink();
          void card.current.accept();
        }}
      >
        {copied === "yes" ? "Link Copied" : "Copy Link"}
      </Button>
    );
  } else {
    main = (
      <Button class="ins-main" onClick={accept}>
        Add to Home Screen
      </Button>
    );
  }

  const footer =
    step === 0 ? (
      <>
        {/* close(): Not Now before the button, only a close after it (Copy Link or Open in Chrome keep the sheet up) */}
        <Button variant="ghost" class="ins-later" onClick={() => leave((c) => c.close())}>
          Not Now
        </Button>
        {main}
      </>
    ) : (
      <>
        <Button variant="ghost" class="ins-later" onClick={() => (step === 1 ? leave((c) => c.close()) : setStep((step - 1) as InstallStep))}>
          {step === 1 ? "Not Now" : "Back"}
        </Button>
        <Button class="ins-main" onClick={() => (step === 3 ? leave((c) => c.close()) : setStep((step + 1) as InstallStep))}>
          {step === 3 ? "Got It" : "Next"}
        </Button>
      </>
    );

  return (
    <Sheet
      open={open}
      onClose={() => leave((c) => c.close())}
      title={label}
      hideTitle
      noClose
      variant="modal"
      width={440}
      class="hb-install"
      headActions={
        <span class="ins-top" aria-hidden="true">
          <span class="eyebrow">{label}</span>
          {step === 0 ? null : <StepBars step={step} />}
        </span>
      }
      footer={footer}
    >
      {step === 0 ? <HomeScreen t={t} badge={s.pings} /> : <StepArt step={step} guide={guide} />}
      <p class="ins-line" aria-live="polite">
        {step === 0 ? (
          pitchLine(t, s.pings)
        ) : (
          <>
            <span class="sr">Step {step} of 3. </span>
            {stepLine(step, guide)}
          </>
        )}
      </p>
      {step === 0 && inApp ? (
        <p class="ins-sub">
          <Icon name="arrow-ur" />
          Open this page in {s.os === "android" ? "Chrome" : "Safari"} first.
        </p>
      ) : null}
      {copied === "failed" && s.pageHref ? <span class="ins-url">{s.pageHref}</span> : null}
    </Sheet>
  );
}
